import { type FC } from "react";

import { Mail } from "lucide-react";

import { PeopleSelector } from "@/shared/components/PeopleSelector";
import { Button } from "@/shared/components/ui/button";
import { FieldError } from "@/shared/components/ui/FieldError";
import { Input } from "@/shared/components/ui/input";
import { PhoneNumberInput } from "@/shared/components/ui/phone-number-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Separator } from "@/shared/components/ui/separator";
import { RELATIONSHIP_OPTIONS } from "@/shared/constants/relationshipOptions";
import { useUser } from "@/shared/hooks/useAppContext";
import { useDeceasedUserData } from "@/shared/hooks/useDeceasedUserData";
import { usePersonInvitations } from "@/shared/hooks/useEstateInvitations";
import { useInviteeForm } from "@/shared/hooks/useInviteeForm";
import { isValidEmail } from "@/shared/utils/validators";

import { Label } from "@/shared/components/ui/label";

import { TriageModalStepLayout } from "./TriageModalStepLayout";

type InviteRole = "executor" | "administrator" | "trustee";

interface TriageModalInviteStepProps {
  personFirstName: string;
  estateId: number;
  inviteeName: string;
  inviteRole: InviteRole;
  onBack: () => void;
  onContinue: () => void;
}

const ROLE_INTROS: Record<InviteRole, (name: string) => string> = {
  executor: (name) => `The court named ${name} as the person with legal authority over the estate.`,
  administrator: (name) =>
    `The court named ${name} as the person with legal authority over the estate.`,
  trustee: (name) =>
    `The trust names ${name} as trustee, the person with legal authority over trust assets.`,
};

export const TriageModalInviteStep: FC<TriageModalInviteStepProps> = ({
  personFirstName,
  estateId,
  inviteeName,
  inviteRole,
  onBack,
  onContinue,
}) => {
  const personName = personFirstName || "the person who passed";
  const user = useUser();
  const { fullName: decedentFullName } = useDeceasedUserData();
  const { sendInvitation, isSendingInvitation } = usePersonInvitations(estateId);

  const inviterName = user?.first_name || "Someone";

  const {
    contactId,
    pickContact,
    fullName,
    setFullName,
    relationship,
    setRelationship,
    email,
    setEmail,
    phone,
    setPhone,
    errors,
    setErrors,
    saveContact,
  } = useInviteeForm(inviteeName);

  const validate = (): boolean => {
    const next: { fullName?: string; email?: string } = {};
    if (!fullName.trim()) next.fullName = "Enter their full name";
    if (!email.trim()) {
      next.email = "Enter their email address";
    } else if (!isValidEmail(email)) {
      next.email = "Enter a valid email address";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSendInvite = async () => {
    if (!validate()) return;

    try {
      await saveContact();

      await sendInvitation.mutateAsync({
        estateId,
        email: email.trim().toLowerCase(),
        role: "member",
        analytics: {
          inviteSource: "triage",
          inviteeRole: inviteRole,
          hasPhone: !!phone.trim(),
          hasPersonalNote: false,
        },
      });

      onContinue();
    } catch {
      // hooks handle snackbar errors
    }
  };

  const inviteeFirstName = fullName.trim().split(/\s+/)[0] || "them";
  const roleIntro = ROLE_INTROS[inviteRole](inviteeFirstName);

  return (
    <TriageModalStepLayout
      title={`Invite ${inviteeFirstName} to ${personName}'s estate`}
      description={`${roleIntro} Banks and courts need ${inviteeFirstName}'s sign-off before money moves or next steps get approved. Invite ${inviteeFirstName} and keep everything organized in one place.`}
      activeStep={4}
      showInvite
      onBack={onBack}
      actionButton={
        <Button variant="brand" size="md" onClick={handleSendInvite} disabled={isSendingInvitation}>
          Send invite
        </Button>
      }
    >
      <Separator />

      <PeopleSelector
        mode="single"
        label="Choose from people"
        placeholder="Choose someone already in People"
        value={contactId}
        onChange={pickContact}
      />

      <div className="flex flex-col gap-4 sm:flex-row">
        <div className="flex flex-1 flex-col gap-1.5">
          <Label>Full name</Label>
          <Input
            value={fullName}
            onChange={(e) => {
              setFullName(e.target.value);
              setErrors((prev) => ({ ...prev, fullName: undefined }));
            }}
            placeholder="Enter full name"
          />
          <FieldError message={errors.fullName} />
        </div>
        <div className="flex flex-col gap-1.5 sm:w-[220px] sm:shrink-0">
          <Label>Relationship to {personFirstName || "decedent"}</Label>
          <Select value={relationship} onValueChange={setRelationship}>
            <SelectTrigger>
              <SelectValue placeholder="Select a relationship" />
            </SelectTrigger>
            <SelectContent>
              {RELATIONSHIP_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex flex-col gap-4 sm:flex-row">
        <div className="flex flex-1 flex-col gap-1.5">
          <Label>Email</Label>
          <Input
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setErrors((prev) => ({ ...prev, email: undefined }));
            }}
            placeholder="example@email.com"
          />
          <FieldError message={errors.email} />
        </div>
        <div className="flex flex-1 flex-col gap-1.5">
          <Label>
            Phone <span className="text-tertiary">(optional)</span>
          </Label>
          <PhoneNumberInput value={phone} onChange={setPhone} />
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-primary">
        <div className="flex items-center gap-2 bg-subtle px-4 py-3">
          <Mail className="h-4 w-4 text-secondary" aria-hidden="true" />
          <p className="text-sm text-secondary">Here's the invitation we'll send</p>
        </div>
        <Separator />
        <div className="flex flex-col gap-3 bg-white p-4 text-sm text-primary">
          <p>
            {inviterName} invited you to {decedentFullName || `${personName}'s`} estate on Elayne.
          </p>
          <p>
            Elayne helps families and attorneys settle estates, from getting organized to probate to
            finding and transferring what the person left behind.
          </p>
          <p>
            The account is already set up, documents and details are in place, and the next steps
            are waiting on you. Your legal authority is what moves the estate forward.
          </p>
          <p className="font-medium text-link underline">
            Join {decedentFullName || `${personName}'s`} estate
          </p>
        </div>
      </div>
    </TriageModalStepLayout>
  );
};
